import { useCallback } from 'react';

import { ApiError } from './api';
import { useT, type Translate } from './i18n';

/**
 * What to show someone when a request fails.
 *
 * The server's own `error` text is English and always will be: it is written
 * for logs and for API clients as much as for this interface. So anything the
 * interface needs to say properly — a paused render, mostly — carries a stable
 * `code` alongside it, and the code is what gets translated. Everything else
 * falls through to the server's sentence, which is better than a shrug.
 */
export function errorText(err: unknown, t: Translate, fallbackKey: string): string {
  if (err instanceof ApiError && err.code) {
    const key = `error.${err.code}`;
    const translated = t(key, err.vars);
    // `t` hands back the key itself when nothing is written for it.
    if (translated !== key) return translated;
  }
  if (err instanceof Error && err.message) return err.message;
  return t(fallbackKey);
}

/** `errorText` bound to the current language. */
export function useErrorText(): (err: unknown, fallbackKey: string) => string {
  const t = useT();
  return useCallback((err: unknown, fallbackKey: string) => errorText(err, t, fallbackKey), [t]);
}
